import React, { useState, useCallback, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TextInput, FlatList, TouchableOpacity, ActivityIndicator, Dimensions, Share, Alert, Modal, Pressable, KeyboardAvoidingView, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useTranslation } from 'react-i18next';
import { searchPosts, searchActors, like, unlike, repost, unrepost, getConvoForMembers, getDid, createPost } from '../services/bluesky';
import { extractPlainText } from '../utils/text';
import Hyperlink from '../components/Hyperlink';
import VideoEmbed from '../components/VideoEmbed';
import { Theme, useThemedStyles, useTheme } from '../theme';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface SearchProps {
  onBack: () => void;
  onSelectPost?: (post: any) => void;
  onSelectUser?: (did: string) => void;
  onSearchHashtag?: (hashtag: string) => void;
  onSelectRoom?: (room: any) => void;
  initialQuery?: string;
}

const Search: React.FC<SearchProps> = ({ onBack, onSelectPost, onSelectUser, onSearchHashtag, onSelectRoom, initialQuery = '' }) => {
  const insets = useSafeAreaInsets();
  const [query, setQuery] = useState(initialQuery);
  const [tab, setTab] = useState<'posts' | 'users'>('posts');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [isStartingChat, setIsStartingChat] = useState<string | null>(null);
  const [postMenuVisible, setPostMenuVisible] = useState(false);
  const [selectedPostForMenu, setSelectedPostForMenu] = useState<any>(null);
  const [quoteModalVisible, setQuoteModalVisible] = useState(false);
  const [quoteText, setQuoteText] = useState('');
  const [isQuoteSending, setIsQuoteSending] = useState(false);
  const lastSearchQuery = useRef('');
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  const handleSearch = useCallback(async (isNew = true, currentQuery = query) => {
    const trimmedQuery = currentQuery.trim();
    if (!trimmedQuery) return;
    
    // Prevent duplicate searches with same query
    if (isNew && trimmedQuery === lastSearchQuery.current && results.length > 0) return;
    
    if (isNew) {
      setLoading(true);
      setResults([]);
      lastSearchQuery.current = trimmedQuery;
    } else {
      if (!cursor || loadingMore) return;
      setLoadingMore(true);
    }

    try {
      let res: any;
      if (tab === 'posts') {
        res = await searchPosts(trimmedQuery, isNew ? undefined : cursor);
        // Search API returns posts directly, but we want to wrap them to match Timeline's { post: ... } structure if possible
        // Actually searchPosts returns { posts: [...] } where each item is a post view.
        // Timeline uses { post: ..., reason: ..., reply: ... }
        const formattedPosts = res.posts.map((p: any) => ({ post: p }));
        setResults(prev => isNew ? formattedPosts : [...prev, ...formattedPosts]);
      } else {
        res = await searchActors(trimmedQuery, isNew ? undefined : cursor);
        setResults(prev => isNew ? res.actors : [...prev, ...res.actors]);
      }
      setCursor(res.cursor);
    } catch (e) {
      console.error('Search failed', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [query, tab, cursor, results.length, loadingMore]);

  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
      handleSearch(true, initialQuery);
    }
  }, [initialQuery, handleSearch]);

  const handleLike = async (item: any) => {
    const { post } = item;
    const isLiked = !!post.viewer?.like;
    try {
      if (isLiked) {
        await unlike(post.viewer.like);
      } else {
        const res = await like(post.uri, post.cid);
        post.viewer.like = res.uri;
      }
      
      setResults(prev => prev.map(p => {
        if (p.post?.cid === post.cid) {
          return {
            ...p,
            post: {
              ...p.post,
              likeCount: (p.post.likeCount || 0) + (isLiked ? -1 : 1),
              viewer: { ...p.post.viewer, like: isLiked ? undefined : post.viewer.like }
            }
          };
        }
        return p;
      }));
    } catch (e) {
      console.error('Like failed', e);
    }
  };

  const handleRepost = async (item: any) => {
    const post = item.post;
    const isReposted = !!post.viewer?.repost;
    const previousRepostUri = post.viewer?.repost;
    const previousCount = post.repostCount || 0;

    if (isReposted) {
      // Optimistic UI update
      setResults(prev => prev.map(p => {
        if (p.post.cid === post.cid || p.post.uri === post.uri) {
          return {
            ...p,
            post: {
              ...p.post,
              repostCount: Math.max(0, previousCount - 1),
              viewer: { ...p.post.viewer, repost: undefined }
            }
          };
        }
        return p;
      }));

      try {
        await unrepost(previousRepostUri);
      } catch (e) {
        console.error('Unrepost failed', e);
        // Revert on error
        setResults(prev => prev.map(p => {
          if (p.post.cid === post.cid || p.post.uri === post.uri) {
            return {
              ...p,
              post: {
                ...p.post,
                repostCount: previousCount,
                viewer: { ...p.post.viewer, repost: previousRepostUri }
              }
            };
          }
          return p;
        }));
      }
    } else {
      setSelectedPostForMenu(item);
      setPostMenuVisible(true);
    }
  };

  const executeRepost = async () => {
    if (!selectedPostForMenu) return;
    const { post } = selectedPostForMenu;
    setPostMenuVisible(false);

    // Guard against rapid clicks while an action is pending
    if (post.viewer?.repost === 'pending') return;

    const previousCount = post.repostCount || 0;

    // Optimistic UI update
    setResults(prev => prev.map(p => {
      if (p.post.cid === post.cid || p.post.uri === post.uri) {
        return {
          ...p,
          post: {
            ...p.post,
            repostCount: previousCount + 1,
            viewer: { ...p.post.viewer, repost: 'pending' }
          }
        };
      }
      return p;
    }));

    try {
      const res = await repost(post.uri, post.cid);
      setResults(prev => prev.map(p => {
        if (p.post.cid === post.cid || p.post.uri === post.uri) {
          return {
            ...p,
            post: {
              ...p.post,
              viewer: { ...p.post.viewer, repost: res.data.uri }
            }
          };
        }
        return p;
      }));
    } catch (e) {
      console.error('Repost failed', e);
      // Revert on error
      setResults(prev => prev.map(p => {
        if (p.post.cid === post.cid || p.post.uri === post.uri) {
          return {
            ...p,
            post: {
              ...p.post,
              repostCount: previousCount,
              viewer: { ...p.post.viewer, repost: undefined }
            }
          };
        }
        return p;
      }));
    }
  };

  const executeQuote = () => {
    if (!selectedPostForMenu) return;
    setPostMenuVisible(false);
    setQuoteText('');
    setQuoteModalVisible(true);
  };

  const handleSendQuote = async () => {
    if (!quoteText.trim() || !selectedPostForMenu) return;
    setIsQuoteSending(true);
    try {
      const { post } = selectedPostForMenu;
      await createPost({
        text: quoteText,
        quote: {
          uri: post.uri,
          cid: post.cid
        }
      });
      setQuoteText('');
      setQuoteModalVisible(false);
      Alert.alert('成功', '引用リポストを投稿しました');
    } catch (e) {
      console.error('Quote repost failed', e);
      Alert.alert('エラー', '引用リポストの投稿に失敗しました');
    } finally {
      setIsQuoteSending(false);
    }
  };

  const handleStartChat = async (user: any) => {
    if (!onSelectRoom) return;
    setIsStartingChat(user.did);
    try {
      const convo = await getConvoForMembers([user.did]);
      const currentDid = getDid();
      const otherMember = convo.members.find((m: any) => m.did !== currentDid);
      
      onSelectRoom({
        id: convo.id,
        title: otherMember?.displayName || otherMember?.handle || 'Unknown',
        type: 'convo',
        uri: convo.id,
        otherDid: otherMember?.did,
      });
    } catch (error) {
      console.error('Failed to start chat:', error);
      Alert.alert(t('common.error'), t('search.chatError'));
    } finally {
      setIsStartingChat(null);
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}分前`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}時間前`;
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const renderPostImages = (post: any) => {
    const images = post.embed?.images || post.embed?.media?.images;
    if (!images || images.length === 0) return null;

    if (images.length === 1) {
      const img = images[0];
      const aspectRatio = img.aspectRatio ? img.aspectRatio.width / img.aspectRatio.height : 1;
      return (
        <Image 
          source={{ uri: img.fullsize || img.thumb }} 
          style={[styles.postImage, { aspectRatio: Math.max(0.8, Math.min(1.5, aspectRatio)) }]} 
          contentFit="cover"
        />
      );
    }

    return (
      <View style={styles.imageGrid}>
        {images.slice(0, 4).map((img: any, i: number) => (
          <Image 
            key={i}
            source={{ uri: img.thumb }} 
            style={[
              styles.gridImage, 
              { width: images.length === 2 ? '49.5%' : '32.8%', height: images.length === 2 ? 200 : 120 }
            ]} 
            contentFit="cover"
          />
        ))}
      </View>
    );
  };

  const renderPostVideo = (post: any) => {
    let video = null;
    if (post.embed?.$type === 'app.bsky.embed.video#view') {
      video = post.embed;
    } else if (post.embed?.$type === 'app.bsky.embed.recordWithMedia#view' && post.embed.media?.$type === 'app.bsky.embed.video#view') {
      video = post.embed.media;
    }
    if (!video?.playlist) return null;
    return (
      <View style={styles.postVideoWrapper}>
        <VideoEmbed uri={video.playlist} aspectRatio={video.aspectRatio} />
      </View>
    );
  };

  const renderQuote = (embed: any) => {
    const quote = embed?.record || embed?.record?.record;
    if (!quote || (quote.$type !== 'app.bsky.embed.record#viewRecord' && quote.$type !== 'app.bsky.feed.defs#postView')) return null;

    return (
      <TouchableOpacity 
        style={styles.quoteBox}
        onPress={() => onSelectPost?.(quote)}
      >
        <View style={{ flexDirection: 'row' }}>
          <View style={{ flex: 1 }}>
            <View style={styles.quoteHeader}>
              <Image source={{ uri: quote.author.avatar }} style={styles.quoteAvatar} />
              <Text style={styles.quoteAuthor} numberOfLines={1}>
                {quote.author.displayName || quote.author.handle}
              </Text>
            </View>
            <Text style={styles.quoteText} numberOfLines={3}>
              {extractPlainText(quote)}
            </Text>
          </View>
          
          {/* Quoted Post Image Preview */}
          {(quote.embed?.images && quote.embed.images.length > 0) && (
            <Image 
              source={{ uri: quote.embed.images[0].thumb }} 
              style={styles.quoteImagePreview}
            />
          )}
          {quote.embed?.$type === 'app.bsky.embed.recordWithMedia#view' && 
           quote.embed.media?.images && quote.embed.media.images.length > 0 && (
            <Image 
              source={{ uri: quote.embed.media.images[0].thumb }} 
              style={styles.quoteImagePreview}
            />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const renderPost = ({ item }: { item: any }) => {
    const { post } = item;
    const record = (post.record?.value || post.record || {}) as any;
    const displayText = extractPlainText(post) || '';

    return (
      <View style={styles.postCard}>
        <TouchableOpacity onPress={() => onSelectPost?.(post)} activeOpacity={0.7}>
          <View style={styles.postHeader}>
            <Image source={{ uri: post.author.avatar }} style={styles.postAuthorIcon} />
            <View style={styles.authorInfo}>
              <Text style={styles.postAuthorName} numberOfLines={1}>
                {post.author.displayName || post.author.handle}
              </Text>
              <Text style={styles.postTime}>{formatTime(record.createdAt || post.indexedAt)}</Text>
            </View>
            <TouchableOpacity style={styles.moreButton}>
              <Ionicons name="ellipsis-horizontal" size={20} color={theme.colors.textMuted} />
            </TouchableOpacity>
          </View>

          <Hyperlink 
            linkDefault={true} 
            linkStyle={{ color: theme.colors.primary, textDecorationLine: 'underline' }} 
            style={styles.postText}
            onHashtagPress={onSearchHashtag}
          >
            {displayText}
          </Hyperlink>
          
          {renderPostImages(post)}
          {renderPostVideo(post)}
          {renderQuote(post.embed)}
        </TouchableOpacity>

        <View style={styles.postFooter}>
          <View style={styles.footerLeft}>
            <TouchableOpacity style={styles.footerAction} onPress={() => handleLike(item)}>
              <Ionicons 
                name={post.viewer?.like ? "heart" : "heart-outline"} 
                size={22} 
                color={post.viewer?.like ? theme.colors.danger : theme.colors.textMuted} 
              />
              <Text style={[styles.footerActionText, post.viewer?.like && { color: theme.colors.danger }]}>
                {post.likeCount || ''}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.footerAction} onPress={() => handleRepost(item)}>
              <Ionicons 
                name="repeat" 
                size={22} 
                color={post.viewer?.repost ? theme.colors.primary : theme.colors.textMuted} 
              />
              <Text style={[styles.footerActionText, post.viewer?.repost && { color: theme.colors.primary }]}>
                {post.repostCount || ''}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const renderUser = ({ item }: { item: any }) => (
    <View style={styles.userItemContainer}>
      <TouchableOpacity style={styles.userItem} onPress={() => onSelectUser?.(item.did)}>
        <Image source={{ uri: item.avatar }} style={styles.avatar} cachePolicy="memory-disk" />
        <View style={styles.userText}>
          <Text style={styles.displayName} numberOfLines={1}>{item.displayName || item.handle}</Text>
          <Text style={styles.handle} numberOfLines={1}>@{item.handle}</Text>
          {item.description && <Text style={styles.description} numberOfLines={1}>{item.description}</Text>}
        </View>
      </TouchableOpacity>
      <TouchableOpacity 
        style={styles.userTalkButton} 
        onPress={() => handleStartChat(item)}
        disabled={isStartingChat === item.did}
      >
        {isStartingChat === item.did ? (
          <ActivityIndicator size="small" color={theme.colors.primary} />
        ) : (
          <Ionicons name="chatbubble-outline" size={24} color={theme.colors.primary} />
        )}
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Ionicons name="chevron-back" size={28} color={theme.colors.text} />
        </TouchableOpacity>
        <View style={styles.searchBar}>
          <Ionicons name="search" size={20} color={theme.colors.textMuted} style={styles.searchIcon} />
          <TextInput
            style={styles.input}
            placeholder={t('search.placeholder')}
            value={query}
            onChangeText={setQuery}
            onSubmitEditing={() => handleSearch(true)}
            returnKeyType="search"
            autoFocus={!initialQuery}
            placeholderTextColor={theme.colors.textMuted}
          />
          {query.length > 0 && (
            <TouchableOpacity onPress={() => setQuery('')}>
              <Ionicons name="close-circle" size={20} color={theme.colors.textMuted} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      <View style={styles.tabBar}>
        <TouchableOpacity 
          style={[styles.tab, tab === 'posts' && styles.activeTab]} 
          onPress={() => { setTab('posts'); setResults([]); setCursor(undefined); lastSearchQuery.current = ''; }}
        >
          <Text style={[styles.tabText, tab === 'posts' && styles.activeTabText]}>{t('search.posts')}</Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.tab, tab === 'users' && styles.activeTab]} 
          onPress={() => { setTab('users'); setResults([]); setCursor(undefined); lastSearchQuery.current = ''; }}
        >
          <Text style={[styles.tabText, tab === 'users' && styles.activeTabText]}>{t('search.users')}</Text>
        </TouchableOpacity>
      </View>

      {loading && results.length === 0 ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      ) : (
        <FlatList
          data={results}
          keyExtractor={(item) => (item.post?.cid || item.did) + (item.post?.indexedAt || '')}
          renderItem={tab === 'posts' ? renderPost : renderUser}
          onEndReached={() => handleSearch(false)}
          onEndReachedThreshold={0.5}
          ListFooterComponent={loadingMore ? <ActivityIndicator style={{ margin: 20 }} color={theme.colors.primary} /> : null}
          contentContainerStyle={styles.listContent}
          ListEmptyComponent={
            !loading && query ? (
              <View style={styles.center}>
                <Text style={styles.emptyText}>結果が見つかりませんでした</Text>
              </View>
            ) : null
          }
        />
      )}

      {/* Repost Menu Modal */}
      <Modal
        visible={postMenuVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setPostMenuVisible(false)}
      >
        <Pressable 
          style={styles.modalOverlay} 
          onPress={() => setPostMenuVisible(false)}
        >
          <View style={styles.menuContainer}>
            <TouchableOpacity 
              style={styles.menuItem} 
              onPress={executeRepost}
            >
              <Ionicons 
                name={selectedPostForMenu?.post?.viewer?.repost ? "repeat" : "repeat-outline"} 
                size={24} 
                color={selectedPostForMenu?.post?.viewer?.repost ? theme.colors.success : theme.colors.text} 
              />
              <Text style={[styles.menuText, selectedPostForMenu?.post?.viewer?.repost && { color: theme.colors.success }]}>
                {selectedPostForMenu?.post?.viewer?.repost ? t('timeline.unrepost') || 'リポストを取消' : t('timeline.repost') || 'リポスト'}
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.menuItem} 
              onPress={executeQuote}
            >
              <Ionicons name="chatbubble-outline" size={24} color={theme.colors.text} />
              <Text style={styles.menuText}>{t('timeline.quote') || '引用リポスト'}</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={[styles.menuItem, styles.cancelItem]} 
              onPress={() => setPostMenuVisible(false)}
            >
              <Text style={styles.cancelText}>{t('common.cancel') || 'キャンセル'}</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Modal>

      {/* Quote Modal */}
      <Modal
        visible={quoteModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setQuoteModalVisible(false)}
      >
        <KeyboardAvoidingView 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.quoteModalContainer}
        >
          <View style={styles.quoteModalContent}>
            <View style={styles.quoteModalHeader}>
              <TouchableOpacity onPress={() => setQuoteModalVisible(false)}>
                <Text style={styles.quoteCancelText}>{t('common.cancel') || 'キャンセル'}</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.quoteSendButton, !quoteText.trim() && styles.quoteSendButtonDisabled]}
                onPress={handleSendQuote}
                disabled={!quoteText.trim() || isQuoteSending}
              >
                {isQuoteSending ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.quoteSendText}>{t('profile.save') || '投稿'}</Text>
                )}
              </TouchableOpacity>
            </View>
            
            {selectedPostForMenu && (
              <View style={styles.quoteTargetPreview}>
                <Image source={{ uri: selectedPostForMenu.post.author?.avatar }} style={styles.quoteTargetAvatar as any} />
                <View style={styles.quoteTargetInfo}>
                  <Text style={styles.quoteTargetAuthor}>@{selectedPostForMenu.post.author?.handle} を引用</Text>
                  <Text style={styles.quoteTargetText} numberOfLines={2}>{extractPlainText(selectedPostForMenu.post)}</Text>
                </View>
              </View>
            )}

            <TextInput
              style={styles.quoteInput}
              placeholder="コメントを追加"
              placeholderTextColor={theme.colors.textMuted}
              multiline
              autoFocus
              value={quoteText}
              onChangeText={setQuoteText}
              maxLength={300}
            />
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: theme.colors.background },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  menuContainer: {
    backgroundColor: theme.colors.card,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    paddingBottom: 40,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 15,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  menuText: {
    fontSize: 18,
    marginLeft: 15,
    color: theme.colors.text,
  },
  cancelItem: {
    borderBottomWidth: 0,
    marginTop: 10,
    justifyContent: 'center',
  },
  cancelText: {
    fontSize: 18,
    color: theme.colors.danger,
    fontWeight: 'bold',
  },
  quoteModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  quoteModalContent: {
    backgroundColor: theme.colors.card,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: '90%',
    padding: 15,
  },
  quoteModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  quoteCancelText: {
    fontSize: 16,
    color: theme.colors.text,
  },
  quoteSendButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 20,
  },
  quoteSendButtonDisabled: {
    opacity: 0.6,
  },
  quoteSendText: {
    color: theme.colors.primaryText,
    fontWeight: 'bold',
    fontSize: 16,
  },
  quoteTargetPreview: {
    flexDirection: 'row',
    marginBottom: 15,
    padding: 10,
    backgroundColor: theme.colors.cardSecondary,
    borderRadius: 12,
  },
  quoteTargetAvatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: theme.colors.cardSecondary,
  },
  quoteTargetInfo: {
    flex: 1,
    marginLeft: 10,
  },
  quoteTargetAuthor: {
    fontSize: 12,
    color: theme.colors.textMuted,
    marginBottom: 2,
  },
  quoteTargetText: {
    fontSize: 14,
    color: theme.colors.text,
  },
  quoteInput: {
    flex: 1,
    fontSize: 18,
    textAlignVertical: 'top',
    color: theme.colors.text,
  },
  header: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    padding: 10, 
    backgroundColor: theme.colors.card,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  backButton: { marginRight: 10 },
  searchBar: { 
    flex: 1, 
    flexDirection: 'row', 
    alignItems: 'center', 
    backgroundColor: theme.colors.input, 
    borderRadius: 20, 
    paddingHorizontal: 12, 
    height: 40,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  searchIcon: { marginRight: 8 },
  input: { flex: 1, fontSize: 16, color: theme.colors.text },
  tabBar: { flexDirection: 'row', backgroundColor: theme.colors.card, borderBottomWidth: 0.5, borderBottomColor: theme.colors.border },
  tab: { flex: 1, paddingVertical: 12, alignItems: 'center' },
  activeTab: { borderBottomWidth: 2, borderBottomColor: theme.colors.primary },
  tabText: { fontSize: 16, color: theme.colors.textMuted },
  activeTabText: { color: theme.colors.primary, fontWeight: 'bold' },
  
  // Timeline styles
  listContent: {
    paddingBottom: 20,
  },
  postCard: {
    backgroundColor: theme.colors.card,
    marginBottom: 8,
    paddingVertical: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  postHeader: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    alignItems: 'center',
    marginBottom: 10,
  },
  postAuthorIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.cardSecondary,
    marginRight: 10,
  },
  authorInfo: {
    flex: 1,
  },
  postAuthorName: {
    fontSize: 15,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  postTime: {
    fontSize: 11,
    color: theme.colors.textMuted,
    marginTop: 2,
  },
  moreButton: {
    padding: 4,
  },
  postText: {
    paddingHorizontal: 16,
    fontSize: 15,
    color: theme.colors.text,
    marginBottom: 10,
    lineHeight: 22,
  },
  postImage: {
    width: SCREEN_WIDTH,
    backgroundColor: theme.colors.cardSecondary,
  },
  postVideoWrapper: {
    paddingHorizontal: 16,
    marginTop: 8,
  },
  imageGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 16,
    gap: 4,
  },
  gridImage: {
    backgroundColor: theme.colors.cardSecondary,
    borderRadius: 4,
  },
  quoteBox: {
    marginHorizontal: 16,
    marginTop: 10,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.cardSecondary,
    overflow: 'hidden',
  },
  quoteImagePreview: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginLeft: 10,
    backgroundColor: theme.colors.border,
  },
  quoteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  quoteAvatar: {
    width: 20,
    height: 20,
    borderRadius: 10,
    marginRight: 6,
  },
  quoteAuthor: {
    fontSize: 13,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  quoteText: {
    fontSize: 13,
    color: theme.colors.textMuted,
    lineHeight: 18,
  },
  postFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 12,
    marginTop: 8,
  },
  footerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  footerAction: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 24,
  },
  footerActionText: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginLeft: 4,
  },

  // User styles
  userItemContainer: { 
    flexDirection: 'row', 
    backgroundColor: theme.colors.card, 
    borderBottomWidth: 0.5, 
    borderBottomColor: theme.colors.border, 
    alignItems: 'center' 
  },
  userItem: { 
    flex: 1,
    flexDirection: 'row', 
    padding: 15, 
    alignItems: 'center' 
  },
  userTalkButton: {
    padding: 15,
  },
  avatar: { width: 40, height: 40, borderRadius: 20, marginRight: 10, backgroundColor: theme.colors.cardSecondary },
  userText: { flex: 1 },
  displayName: { fontSize: 16, fontWeight: 'bold', color: theme.colors.text },
  handle: { fontSize: 14, color: theme.colors.textMuted },
  description: { fontSize: 14, color: theme.colors.textMuted, marginTop: 2 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20, backgroundColor: theme.colors.background },
  emptyText: { fontSize: 16, color: theme.colors.textMuted },
});

export default Search;
